/**
 * @deprecated Prefer `<SEO />` from "@/components/SEO" — it uses Helmet so the
 * tags are picked up by the SSR static-shell generator and arrive on the
 * initial HTML. This hook keeps existing private/legal pages working but does
 * NOT participate in static shells.
 */
import { useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";

import { SITE_URL } from "@/lib/site-url";

const BASE_URL = SITE_URL;
const OG_IMAGE = `${BASE_URL}/og-retail-lemon.jpg`;

interface SEOConfig {
  title: { es: string; en: string };
  description: { es: string; en: string };
  path: string;
  pathEs?: string;
  pathEn?: string;
  noindex?: boolean;
  type?: string;
  image?: string;
  imageAlt?: { es: string; en: string };
  keywords?: { es: string; en: string };
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  /** La página ya emite su SEO con `<SEO />`; no tocar el <head>. */
  skip?: boolean;
}

const setMeta = (attr: string, name: string, content: string) => {
  let el = document.querySelector(`meta[${attr}='${name}']`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

// Solo tocamos los <link> creados por este hook. Los que renderiza React
// (head() de la ruta / Helmet) nunca se eliminan desde aquí: quitarlos del DOM
// a sus espaldas provoca "Cannot read properties of null (reading 'removeChild')".
const OWN_ATTR = "data-useseo";

const setLink = (rel: string, href: string, extra?: Record<string, string>) => {
  const selector = extra
    ? `link[${OWN_ATTR}][rel='${rel}'][${Object.entries(extra).map(([k, v]) => `${k}='${v}'`).join("][")}]`
    : `link[${OWN_ATTR}][rel='${rel}']`;
  let el = document.querySelector(selector) as HTMLLinkElement;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(OWN_ATTR, "1");
    if (extra) Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.href = href;
  return el;
};

const removeHreflangTags = () => {
  document.querySelectorAll(`link[${OWN_ATTR}][rel='alternate'][hreflang]`).forEach((el) => el.remove());
};

const removeJsonLd = (id: string) => {
  document.getElementById(id)?.remove();
};

export const useSEO = (config: SEOConfig) => {
  const { lang } = useLanguage();

  useEffect(() => {
    // Si la página ya gestiona su SEO con <SEO /> (Helmet) no duplicamos nada.
    if (config.skip) return;
    const title = config.title[lang];
    const description = config.description[lang];
    const esPath = config.pathEs || config.path;
    const enPath = config.pathEn || config.path;
    const fullUrl = `${BASE_URL}${lang === "es" ? esPath : enPath}`;
    const image = config.image || OG_IMAGE;

    document.title = title;

    setMeta("name", "description", description);
    setMeta("name", "robots", config.noindex ? "noindex, nofollow" : "index, follow");
    if (config.keywords) {
      setMeta("name", "keywords", config.keywords[lang]);
    }

    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", fullUrl);
    setMeta("property", "og:type", config.type || "website");
    setMeta("property", "og:image", image);
    setMeta("property", "og:locale", lang === "es" ? "es_ES" : "en_US");
    setMeta("property", "og:locale:alternate", lang === "es" ? "en_US" : "es_ES");

    const imageAltText = config.imageAlt?.[lang] || "Mentoring Retail 360º — Consultoría para tiendas y negocios retail";
    setMeta("property", "og:image:alt", imageAltText);

    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", image);
    setMeta("name", "twitter:card", "summary_large_image");

    setLink("canonical", fullUrl);

    removeHreflangTags();
    setLink("alternate", `${BASE_URL}${esPath}`, { hreflang: "es" });
    setLink("alternate", `${BASE_URL}${enPath}`, { hreflang: "en" });
    setLink("alternate", `${BASE_URL}${esPath}`, { hreflang: "x-default" });

    if (config.jsonLd) {
      removeJsonLd("seo-jsonld");
      const script = document.createElement("script");
      script.id = "seo-jsonld";
      script.type = "application/ld+json";
      script.textContent = JSON.stringify(config.jsonLd);
      document.head.appendChild(script);
    }

    return () => {
      removeHreflangTags();
      removeJsonLd("seo-jsonld");
    };
  }, [lang, config.path]); // eslint-disable-line react-hooks/exhaustive-deps
};
